import createAsyncGetters from '../config/asyncGetters/createAsyncGetters';
import { AvatarFrameId } from '../profile/frames';
import {
  getPlayerProfile,
  getPlayerProfileFromLocale,
} from '../profile/profile.getters';
import {
  getWeeklyLeaderboardFriendsIDs,
  sortWeeklyLeaderboardRanking,
  WeeklyLeaderboardRankingArgs,
} from './weeklyLeaderboard.getters';
import { weeklyLeaderboardGlobalRankingSize } from './weeklyLeaderboard.ruleset';

export const weeklyLeaderboardAsyncGetters = createAsyncGetters({
  getWeeklyLeaderboardRanking: async (
    args: WeeklyLeaderboardRankingArgs,
    api,
  ) => {
    const players = args.opponents.length
      ? await api.searchPlayers({
          where: { id: { isOneOf: [...args.opponents] } },
          fields: [
            'weeklyLeaderboardEntries',
            'publicProfileName',
            'publicProfilePhoto',
            'profileAvatarFrame',
          ],
          limit: args.opponents.length,
        })
      : { results: [] };

    // Fill up the list with the entries
    const list = players.results.map((player) => {
      const entry = player.weeklyLeaderboardEntries?.find(
        (entry) => entry.eventId === args.eventId,
      );

      const profile = getPlayerProfileFromLocale(
        args.locale,
        player.publicProfileName,
        player.publicProfilePhoto,
      );

      return {
        // if user has not entry yet, their score is 0
        score: entry ? entry.score : 0,
        joinedAt: entry ? entry.joinedAt : 0,
        eventId: args.eventId,
        id: player.id,
        name: profile.name,
        photo: profile.photo,
        frame: player.profileAvatarFrame as AvatarFrameId,
      };
    });

    // Push the user entry to the list
    list.push({
      id: api.getUserID(),
      eventId: args.eventId,
      score: args.score,
      joinedAt: args.joinedAt,
      name: args.ownProfile.name,
      photo: args.ownProfile.photo,
      frame: args.ownFrame,
    });

    // Return the list sorted by ranking
    return sortWeeklyLeaderboardRanking(list);
  },

  getWeeklyLeaderboardGlobalRanking: async (_: void, api) => {
    const players = await api.searchPlayers({
      where: {
        match2Level: {
          greaterThanOrEqual: 1,
        },
        hasPublicProfile: {
          is: true,
        },
      },
      sort: [
        {
          field: 'match2Level',
          order: 'desc',
        },
        {
          field: 'leaguesAllTimeScore',
          order: 'desc',
        },
      ],
      limit: weeklyLeaderboardGlobalRankingSize,
    });

    const list = players.results.map((player) => {
      return {
        id: player.id,
        name: player.publicProfileName,
        photo: player.publicProfilePhoto,
        frame: player.profileAvatarFrame as AvatarFrameId,
        level: player.match2Level,
        leaguesAllTimeScore: player.leaguesAllTimeScore,
      };
    });

    return list;
  },

  getWeeklyLeaderboardFriendsRanking: async (_: void, api) => {
    const state = await api.getOwnState();
    const friends = getWeeklyLeaderboardFriendsIDs(state);

    const players = await api.searchPlayers({
      where: {
        match2Level: {
          greaterThanOrEqual: 1,
        },
        id: {
          isOneOf: [...friends, state.id],
        },
      },
      sort: [
        {
          field: 'match2Level',
          order: 'desc',
        },
        {
          field: 'leaguesAllTimeScore',
          order: 'desc',
        },
      ],
      limit: weeklyLeaderboardGlobalRankingSize,
    });

    const list = players.results.map((player) => {
      const userProfile = getPlayerProfile(
        state,
        player.publicProfileName,
        player.publicProfilePhoto,
        undefined, // We should not fallback to the private profile here
        undefined,
        player.id === state.id,
      );
      return {
        id: player.id,
        name: userProfile.name,
        photo: userProfile.photo,
        frame: player.profileAvatarFrame as AvatarFrameId,
        level: player.match2Level,
        leaguesAllTimeScore: player.leaguesAllTimeScore,
      };
    });

    // Return the list sorted by ranking
    return list;
  },
});
